import { NgModule } from '@angular/core';
import { Route, RouterModule, Routes } from '@angular/router';
import { KPIDashboardComponent } from '~lmo/kpidashboard/kpidashboard.component';
import { AdminOrStandardRouteGuard } from '~services/admin-or-standard-route-guard';
import { AdminRouteGuard } from '~services/admin-route-guard';
import * as fromComponents from './components';
import * as fromContainers from './containers';
import * as constants from './lmo-routing.constants';

const fracFracStagesModalRoutes: Route = {
  canActivate: [AdminOrStandardRouteGuard],
  children: [
    {
      component: fromComponents.LmoFracStagesEditComponent,
      path: `stage/:${constants.STAGE_NUMBER}`,
    },
  ],
  component: fromContainers.LmoFracStagesModalComponent,
  path: 'lmo_frac_stages_modal',
};

const fracFracHomeModalRoutes: Route = {
  canActivate: [AdminOrStandardRouteGuard],
  children: [
    {
      children: [
        {
          component: fromComponents.LmoLoaderClosedScheduleSpecificLoaderComponent,
          path: `:${constants.LOADER_CLOSED_HOURS_ID}`,
        },
      ],
      component: fromComponents.LmoLoaderClosedScheduleComponent,
      path: 'lmo_loader_hours',
    },
    {
      component: fromComponents.LmoResetCurrentStockComponent,
      path: 'reset_current_stock',
    },
    {
      component: fromComponents.LmoCompleteStageComponent,
      path: 'complete_current_stage',
    },
    {
      component: fromComponents.LmoFracAutopilotEngageComponent,
      path: 'autopilot_toggle',
    },
    {
      component: fromComponents.LmoLoaderStatusReportComponent,
      path: 'create_loader_report',
    },
    {
      component: fromComponents.LmoWellStatusReportComponent,
      path: 'create_frac_report',
    },
    {
      canActivate: [AdminRouteGuard],
      children: [
        {
          path: '',
          pathMatch: 'full',
          redirectTo: 'add',
        },
        {
          component: fromComponents.LmoFracFctvAddComponent,
          path: 'add',
        },
        {
          children: [
            {
              component: fromComponents.LmoFracFctvManageIdComponent,
              path: `:${constants.FCTV_ID}`,
            },
          ],
          component: fromComponents.LmoFracFctvManageComponent,
          path: 'manage',
        },
      ],
      component: fromComponents.LmoFracFctvComponent,
      path: 'trucking_vendor',
    },
    {
      canActivate: [AdminOrStandardRouteGuard],
      children: [
        {
          component: fromComponents.LmoFracFctvOverflowComponent,
          path: 'overflow_vendors',
        },
      ],
      component: fromComponents.LmoFracFctvComponent,
      path: 'trucking_vendor',
    },
    {
      children: [
        {
          path: '',
          pathMatch: 'full',
          redirectTo: 'add',
        },
        {
          component: fromComponents.LmoFracFcpoAddComponent,
          path: 'add',
        },
        {
          children: [
            {
              component: fromComponents.LmoFracFcpoManageIdComponent,
              path: `:${constants.FCPO_ID}`,
            },
          ],
          component: fromComponents.LmoFracFcpoManageComponent,
          path: 'manage',
        },
        {
          component: fromComponents.LmoFracFcpoManageLoadersComponent,
          path: 'loaders',
        },
        {
          component: fromComponents.LmoFracFcpoManagePosComponent,
          path: 'manage_purchase_orders',
        },
        {
          component: fromComponents.LmoFracFcpoLoaderDirectionsComponent,
          path: 'loader_directions',
        },
      ],
      component: fromComponents.LmoFracFcpoComponent,
      path: 'purchase_orders',
    },
  ],
  component: fromContainers.LmoFracHomeModalComponent,
  path: 'lmo_frac_home_modal',
};

const fracOrdersRoutes: Route = {
  children: [
    {
      path: '',
      pathMatch: 'full',
      redirectTo: 'pending',
    },
    {
      component: fromComponents.LmoOrdersPendingComponent,
      path: 'pending',
    },
    {
      component: fromComponents.LmoOrdersInProgressComponent,
      path: 'in-progress',
    },
    {
      component: fromComponents.LmoOrdersCompletedComponent,
      path: 'complete',
    },
    {
      component: fromComponents.LmoOrderDetailsComponent,
      path: `:${constants.ORDER_ID}`,
    },
  ],
  component: fromContainers.LmoFracDetailOrdersComponent,
  path: 'orders',
};

const fracRoutes: Route = {
  children: [
    {
      children: [
        {
          component: fromContainers.LmoFracListModalComponent,
          path: 'lmo_frac_list_modal',
        },
      ],
      component: fromContainers.LmoFracListComponent,
      path: 'list',
    },
    {
      children: [
        {
          children: [fracFracStagesModalRoutes],
          component: fromContainers.LmoFracDetailStagesComponent,
          path: 'stages',
        },
        {
          children: [fracFracHomeModalRoutes],
          component: fromContainers.LmoFracHomeComponent,
          path: 'home',
        },
        fracOrdersRoutes,
        {
          path: '',
          pathMatch: 'full',
          redirectTo: 'home',
        },
      ],
      component: fromContainers.LmoFracDetailComponent,
      path: `:${constants.FRAC_ID}`,
    },
    {
      path: '',
      pathMatch: 'full',
      redirectTo: 'list',
    },
  ],
  component: fromContainers.LmoFracComponent,
  path: 'frac',
};

const purchaseOrderRoutes: Route = {
  children: [
    {
      path: '',
      pathMatch: 'full',
      redirectTo: 'list',
    },
    {
      component: fromComponents.LmoPurchaseOrdersComponent,
      path: 'list',
    },
    {
      component: fromComponents.LmoPurchaseOrderDetailComponent,
      path: `detail/:${constants.PURCHASE_ORDER_ID}`,
    },
  ],
  path: 'purchase_order',
};

const routes: Routes = [
  {
    component: fromComponents.LmoTrainingVideosComponent,
    path: 'training-videos',
  },
  {
    canActivate: [AdminRouteGuard],
    loadChildren: () => import('src/app/lmo/loader/loader.module').then((m) => m.LoaderModule),
    path: 'loaders',
  },
  {
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'frac',
      },
      fracRoutes,
    ],
    component: fromContainers.LmoComponent,
    path: '',
    pathMatch: 'prefix',
  },
  {
    canActivate: [AdminRouteGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'purchase_order',
      },
      purchaseOrderRoutes,
    ],
    component: fromContainers.LmoComponent,
    path: '',
    pathMatch: 'prefix',
  },
  {
    component: KPIDashboardComponent,
    path: 'kpi_dashboard',
  },
];

@NgModule({
  exports: [RouterModule],
  imports: [RouterModule.forChild(routes)],
})
export class LmoRoutingModule {}
